import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, UserCheck, Camera, CreditCard, AlertTriangle, Scale, Lock } from 'lucide-react';
import { ENLogo } from '@/components/ENLogo';

export default function TrustSafetyPage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  const sections = [
    {
      icon: UserCheck,
      title: 'Identity Verification',
      content: 'All users on EntrepreNatives must verify their identity. We require government-issued ID and live selfie verification to ensure every person on our platform is real and accountable. Your ID documents are kept private, encrypted, and only accessible to our Trust & Safety team.',
    },
    {
      icon: Camera,
      title: 'Photo Profile Requirement',
      content: 'All vendors must display a real photo of themselves. This creates accountability and builds trust between vendors and consumers. No stock photos or logos as profile pictures — we want real human connections.',
    },
    {
      icon: CreditCard,
      title: 'Escrow Protection',
      content: 'Every transaction on EntrepreNatives is protected by our 50/50 escrow system. 50% down payment when the booking is confirmed, and 50% upon verified completion and customer approval. Funds are held securely by Stripe — EntrepreNatives never directly holds your money.',
    },
    {
      icon: AlertTriangle,
      title: 'Strike System',
      content: 'We maintain a four-strike system for policy violations. Strike 1: Formal warning. Strike 2: Feature restrictions. Strike 3: 30-day suspension. Strike 4: Permanent ban. Severe violations like fraud, identity misrepresentation, or threats result in immediate permanent bans.',
    },
    {
      icon: Scale,
      title: 'Zero Tolerance Policies',
      description: 'We have zero tolerance for:',
      list: [
        'Fraud or scams of any kind',
        'Identity misrepresentation',
        'Escrow manipulation or abuse',
        'Harassment or discrimination',
        'Chargeback abuse',
        'Off-platform payment solicitation',
        'Self-referral schemes or fake account creation',
      ],
    },
    {
      icon: Lock,
      title: 'Data Security',
      content: 'Your personal information is encrypted using industry-standard SSL/TLS. Passwords are hashed with bcrypt. Payment cards are never stored on our servers (handled entirely by Stripe). Verification documents are encrypted at rest and auto-deleted after verification. We comply with PCI DSS Level 1 standards via Stripe.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white" data-testid="trust-safety-page">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative z-10 max-w-4xl mx-auto px-6 pt-8 pb-16">
        <div className="mb-12"><ENLogo size="md" linkTo="/" /></div>

        <motion.div className="mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-[#8B5CF6]/20 border border-[#8B5CF6]/20">
              <Shield size={24} className="text-[#A78BFA]" />
            </div>
            <div>
              <h1 className="font-syne text-3xl font-bold" data-testid="trust-safety-title">Trust & Safety</h1>
              <p className="text-[#64748B] text-sm mt-1">How we keep EntrepreNatives safe, secure, and accountable</p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              data-testid={`trust-section-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#8B5CF6]/20 border border-[#8B5CF6]/10 flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-6 h-6 text-[#A78BFA]" />
                </div>
                <div>
                  <h2 className="font-syne text-lg font-bold text-white mb-3">{section.title}</h2>
                  {section.content && (
                    <p className="text-[#9CA3AF] text-sm leading-relaxed">{section.content}</p>
                  )}
                  {section.description && (
                    <p className="text-[#9CA3AF] text-sm mb-3">{section.description}</p>
                  )}
                  {section.list && (
                    <ul className="space-y-2">
                      {section.list.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-[#9CA3AF] text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-[#8B5CF6]/10 to-[#3B82F6]/10 border border-white/5 backdrop-blur-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="font-syne text-xl font-bold text-white mb-4 text-center">Why Trust Matters</h2>
          <p className="text-[#9CA3AF] text-sm text-center max-w-2xl mx-auto leading-relaxed">
            Indigenous businesses have historically faced barriers to visibility and economic opportunity.
            EntrepreNatives was built to change that — but only if we maintain the highest standards
            of trust and safety. When you use our platform, you're part of a community committed to
            mutual respect, accountability, and economic empowerment.
          </p>
        </motion.div>

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <Link to="/legal" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[#9CA3AF] hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all">Legal Hub</Link>
          <Link to="/faq" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[#9CA3AF] hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all">FAQ</Link>
          <Link to="/legal/suspension-policy" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[#9CA3AF] hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all">Suspension Policy</Link>
          <Link to="/legal/acceptable-use" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[#9CA3AF] hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all">Acceptable Use</Link>
        </div>
      </div>
    </div>
  );
}
